import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Paper,
} from "@mui/material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Helmet } from "react-helmet-async";
import useAppDispatch from "../../hooks/useAppDispatch";
import useAppSelector from "../../hooks/useAppSelector";
import {
  fetchPurgeLogs,
  lookupUser,
  resetLookup,
} from "../../redux/slices/superadminGdpr";
import GdprPurgeDialog from "../../components/superadmin/GdprPurgeDialog";
import { PurgeDataSnapshot, PurgeUserResult } from "../../types/superadminGdpr";
import useSnackbar from "./SnackbarHook";
import CustomSnackbar from "./SnackBar";

const formatTimestamp = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString("fi-FI");
};

const snapshotSummary = (snap: PurgeDataSnapshot) => {
  const parts: string[] = [];
  if (snap.associations) parts.push(`${snap.associations} tiehoitokuntaa`);
  if (snap.userAssociations) parts.push(`${snap.userAssociations} jäsenyyttä`);
  if (snap.properties) parts.push(`${snap.properties} kiinteistöä`);
  if (snap.invoices) parts.push(`${snap.invoices} laskua`);
  if (snap.vouchers) parts.push(`${snap.vouchers} tositetta`);
  if (snap.messages) parts.push(`${snap.messages} viestiä`);
  if (snap.conversations) parts.push(`${snap.conversations} keskustelua`);
  if (snap.meetingInvites) parts.push(`${snap.meetingInvites} kokouskutsua`);
  if (snap.noticeDeliveries)
    parts.push(`${snap.noticeDeliveries} tiedoksiantoa`);
  if (snap.emailLogs) parts.push(`${snap.emailLogs} sähköpostilokia`);
  return parts.length ? parts.join(", ") : "ei muutoksia";
};

const SuperadminGdprPurge: React.FC = () => {
  const dispatch = useAppDispatch();
  const { lookup, lookupStatus, lookupError, logs, logsStatus, logsError } =
    useAppSelector((state) => state.superadminGdpr);
  const [emailInput, setEmailInput] = useState("");
  const [purgeUserId, setPurgeUserId] = useState<number | null>(null);

  useEffect(() => {
    dispatch(fetchPurgeLogs(undefined));
  }, [dispatch]);
  const {
    openSnackbar,
    snackbarMessage,
    snackbarSeverity,
    progress,
    setOpenSnackbar,
    setSnackbarMessage,
    setSnackbarSeverity,
    duration,
  } = useSnackbar();

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = emailInput.trim();
    if (!email) return;
    await dispatch(lookupUser(email));
  };

  const handleReset = () => {
    setEmailInput("");
    dispatch(resetLookup());
  };

  const handlePurged = (result: PurgeUserResult) => {
    setSnackbarMessage(
      `Käyttäjä poistettu. Poistettuja tiehoitokuntia: ${result.snapshot.associations}.`
    );
    setSnackbarSeverity("success");
    setOpenSnackbar(true);
    setPurgeUserId(null);
    handleReset();
    dispatch(fetchPurgeLogs(undefined));
  };

  const sortedLogs = useMemo(() => logs, [logs]);

  return (
    <>
      <Helmet title="GDPR-poisto" />
      <Typography variant="h3" gutterBottom>
        GDPR — käyttäjän poisto
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Tällä työkalulla voi poistaa käyttäjän henkilötiedot ja valinnaisesti
        myös käyttäjän luomat tiehoitokunnat. Toiminto on peruuttamaton ja
        kirjataan GDPR-poistolokiin.
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Etsi käyttäjä sähköpostilla
          </Typography>
          <Box component="form" onSubmit={handleLookup}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <TextField
                fullWidth
                size="small"
                type="email"
                label="Sähköpostiosoite"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                autoFocus
              />
              <Button
                type="submit"
                variant="contained"
                disabled={
                  lookupStatus === "loading" || emailInput.trim().length < 3
                }
              >
                {lookupStatus === "loading" ? (
                  <CircularProgress size={20} />
                ) : (
                  "Hae"
                )}
              </Button>
              {(lookup || lookupStatus === "notFound") && (
                <Button onClick={handleReset}>Tyhjennä</Button>
              )}
            </Stack>
          </Box>

          {lookupError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {lookupError}
            </Alert>
          )}
          {lookupStatus === "notFound" && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Käyttäjää ei löytynyt antamalla sähköpostilla.
            </Alert>
          )}
        </CardContent>
      </Card>

      {lookup && (
        <Card>
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
            >
              <Box>
                <Typography variant="h6">
                  {lookup.firstName} {lookup.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {lookup.email}
                </Typography>
                <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
                  <Chip
                    size="small"
                    label={`ID: ${lookup.id}`}
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    label={`Luotu: ${new Date(
                      lookup.createdAt
                    ).toLocaleDateString("fi-FI")}`}
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    label={lookup.isVerified ? "Aktivoitu" : "Ei aktivoitu"}
                    color={lookup.isVerified ? "success" : "warning"}
                  />
                  <Chip
                    size="small"
                    label={`Tiehoitokuntia: ${lookup.associationCount}`}
                  />
                  {lookup.isSuperAdmin && (
                    <Chip size="small" label="PÄÄKÄYTTÄJÄ" color="error" />
                  )}
                  {lookup.isProtected && (
                    <Chip size="small" label="SUOJATTU" color="warning" />
                  )}
                </Stack>
              </Box>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteForeverIcon />}
                disabled={lookup.isSuperAdmin || lookup.isProtected}
                onClick={() => setPurgeUserId(lookup.id)}
              >
                Poista käyttäjä (GDPR)
              </Button>
            </Stack>
            {lookup.isSuperAdmin && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Pääkäyttäjää ei voi poistaa tällä työkalulla. Poista
                pääkäyttäjästatus ensin.
              </Alert>
            )}
            {lookup.isProtected && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Käyttäjä on suojattu. Poista suojaus ensin ennen poistoa.
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      <Box mt={4}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Typography variant="h6">Aiemmat poistot</Typography>
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => dispatch(fetchPurgeLogs(undefined))}
            disabled={logsStatus === "loading"}
          >
            Päivitä
          </Button>
        </Stack>
        {logsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {logsError}
          </Alert>
        )}
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Aika</TableCell>
                <TableCell>Suorittaja</TableCell>
                <TableCell>Kohde</TableCell>
                <TableCell>Perustelu</TableCell>
                <TableCell>Poistetut tiehoitokunnat</TableCell>
                <TableCell>Yhteenveto</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logsStatus === "loading" ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Box display="flex" justifyContent="center" py={3}>
                      <CircularProgress size={24} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : sortedLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary" py={2}>
                      Ei poistoja vielä.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedLogs.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{formatTimestamp(row.createdAt)}</TableCell>
                    <TableCell>
                      {row.actorEmail ?? `#${row.actorUserId}`}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {row.targetUserName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.targetUserEmail}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 240 }}>
                      <Tooltip title={row.reason}>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.reason}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {row.deletedAssociations.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      ) : (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {row.deletedAssociations.map((a) => (
                            <Chip
                              key={a.id}
                              size="small"
                              label={a.name}
                              variant="outlined"
                            />
                          ))}
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {snapshotSummary(row.dataSnapshot)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <GdprPurgeDialog
        open={purgeUserId !== null}
        userId={purgeUserId}
        onClose={() => setPurgeUserId(null)}
        onPurged={handlePurged}
      />

      <CustomSnackbar
        open={openSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        progress={progress}
        duration={duration}
        onClose={() => setOpenSnackbar(false)}
      />
    </>
  );
};

export default SuperadminGdprPurge;
